import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import { Box, ButtonBase, Typography, alpha, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { diagnosisScoresSx } from '../../helpers/diagnosisStyle'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { MotivationDiagnosisCard as MotivationDiagnosisCardData, MotivationDiagnosisCategory } from '../../types'
import { DiagnosisGapChip } from '../DiagnosisPrimitives/DiagnosisGapChip'
import { MotivationGapBar, gapBarFlowKeyframesSx, gapBarRevealOnHoverSx } from '../MotivationGapBar/MotivationGapBar'

interface DiagnosisCardProps {
  card: MotivationDiagnosisCardData
  accent: string
  /** Marks the top-priority card of its bucket so the renderer can flag it. */
  isTopOfBucket: boolean
  /** Game app icon shown at the gap bar's goalpost marker in place of the dot. */
  gameIcon?: string
}

/**
 * Generic per-category explanation used when a card's `insightKey` has no
 * locale entry. The key is chosen by the backend and used verbatim as a
 * translation key, so an added or renamed tier would otherwise render as raw
 * snake_case in the card body (as `diagnosis_blind_spot_clear` did once the
 * tier strings were pruned as "unreferenced" — nothing names them statically).
 * Degrading to the category's own sentence keeps the card readable until the
 * new key is translated.
 */
const insightFallbackKey = (category: MotivationDiagnosisCategory): string =>
  category === 'blind_spot' ? 'diagnosis_blind_spot_insight' : 'diagnosis_aligned_insight'

/**
 * One motivation row inside a diagnosis column. Clicking opens the drilldown.
 *
 * The body reads as a coverage shortfall: an outlined gap pill, a bullet-style
 * {@link MotivationGapBar} where the solid fill is what the Creative Ads
 * deliver, a dark goalpost marks the in-game (Game & Live Events) emphasis to
 * reach, and the hatched band between them is the uncovered blind spot. Two
 * colour-coded scores trail the bar. The card itself is light — a plain
 * outlined surface — so the colour cue comes from the bar + pill, not a box.
 */
export const DiagnosisCard: FC<DiagnosisCardProps> = ({ card, accent, isTopOfBucket, gameIcon }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()
  const { openBlindSpot, openAlignment } = useUAInsightsActions()
  const { motivationLabel } = useUATaxonomy()

  // The `gap = gameDesign − creativeAds` is the actual signal that drives the
  // bucket assignment, so make it a first-class visual instead of asking the
  // reader to subtract two side-by-side scores.
  const gameDesignScore = card.scores.gameDesign ?? 0
  const gap = gameDesignScore - card.scores.creativeAds

  return (
    <ButtonBase
      onClick={() => (card.category === 'blind_spot' ? openBlindSpot(card.motivation.id) : openAlignment(card.motivation.id))}
      focusRipple
      sx={{
        textAlign: 'left',
        display: 'block',
        width: '100%',
        borderRadius: borderRadiusDefault,
        ...gapBarFlowKeyframesSx,
        '&:hover, &.Mui-focusVisible': {
          // Lift the card a touch further off its resting elevation — subtle,
          // not a leap — and warm the accent-tinted shadow + background.
          '& .diagnosis-card-paper': {
            backgroundColor: alpha(accent, 0.05),
            borderColor: accent,
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 16px -6px ${alpha(accent, 0.32)}`,
          },
          '& .diagnosis-card-title': { color: accent },
          '& .diagnosis-card-chevron': { opacity: 1, color: accent, transform: 'translateX(2px)' },
          ...gapBarRevealOnHoverSx,
          // On top of the shared reveal, the lone game-icon marker pops out of
          // the track.
          '& .gap-bar-glyph': { transform: 'translateX(-50%) translateY(-4px) scale(1.85) rotate(8deg)', boxShadow: 4 },
        },
        // Settle back to the resting elevation on press so the click reads as
        // a tactile push.
        '&:active': {
          '& .diagnosis-card-paper': {
            transform: 'translateY(0)',
            boxShadow: `0 1px 4px -1px ${alpha(accent, 0.2)}`,
          },
        },
      }}
    >
      <Box
        className="diagnosis-card-paper"
        sx={{
          p: 1.5,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRadius: borderRadiusDefault,
          // A 1px solid border in the category accent on all four sides.
          border: '1px solid',
          borderColor: accent,
          // Resting elevation: a soft accent-tinted shadow so each card reads
          // as a raised surface even before it is hovered.
          boxShadow: `0 2px 8px -4px ${alpha(accent, 0.28)}`,
          willChange: 'transform',
          transition: 'background-color 0.2s ease, box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            className="diagnosis-card-title"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              transition: 'color 0.2s ease',
            }}
          >
            {motivationLabel(card.motivation)}
          </Typography>
          <GapChip gap={gap} accent={accent} category={card.category} insightKey={card.insightKey} isTopOfBucket={isTopOfBucket} />
          <KeyboardArrowRightRoundedIcon
            className="diagnosis-card-chevron"
            sx={{
              fontSize: 18,
              color: alpha(accent, 0.55),
              opacity: 0.7,
              transition: 'opacity 0.18s ease, color 0.18s ease, transform 0.18s ease',
              flexShrink: 0,
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MotivationGapBar
            target={gameDesignScore}
            delivered={card.scores.creativeAds}
            diverging={card.category === 'blind_spot'}
            targetColor={theme.palette.secondary.main}
            deliveredColor={theme.palette.primary.main}
            targetLabel={t('motivation_source_game_design')}
            deliveredLabel={t('motivation_source_creative_ads')}
            targetIcon={gameIcon}
          />
          {/* Score readout ordered to match the bar's left→right reading:
              the Creative Ads fill (left) first, the Game & Live Events
              goalpost (right) last. */}
          <Box className="gap-bar-scores" sx={diagnosisScoresSx}>
            <Box component="span" sx={{ color: 'primary.main' }}>
              {card.scores.creativeAds.toFixed(1)}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled', mx: 0.25 }}>
              /
            </Box>
            <Box component="span" sx={{ color: 'secondary.main' }}>
              {gameDesignScore.toFixed(1)}
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.4 }}>
          {t(card.insightKey, { defaultValue: t(insightFallbackKey(card.category)) })}
        </Typography>
      </Box>
    </ButtonBase>
  )
}

interface GapChipProps {
  /** `gameDesign − creativeAds`. Positive means creatives trail the game. */
  gap: number
  accent: string
  category: MotivationDiagnosisCategory
  /** Sub-classification key used to pick the per-subtype chip label. */
  insightKey: string
  /** Marks the worst blind spot so the chip can headline as "biggest gap". */
  isTopOfBucket: boolean
}

/**
 * Small "Δ" pill that quantifies the gap between game and creative scores.
 *
 * Styled as an outlined pill to match the compare view's Shared / Diverging
 * tags. The single worst blind spot promotes the pill into a filled "Biggest
 * gap" variant so the one priority cue stands out from the outlined crowd.
 *
 * Aligned cards specialise the label so it agrees with the body insight on the
 * same card — when the gap is essentially zero the pill doesn't try to talk
 * about a gap at all:
 *  - `aligned_strong`         → `Strong`         (no value)
 *  - `aligned_balanced`       → `In sync`        (no value, fixes "Amplify gap ±0")
 *  - `aligned_creative_lead`  → `Creatives lead` (with the positive gap)
 *  - `aligned_room_to_amplify`→ `Amplify gap`    (with the gap, default)
 */
const GapChip: FC<GapChipProps> = ({ gap, accent, category, insightKey, isTopOfBucket }) => {
  const { t } = useTranslation('ua-insights')
  const rounded = Math.round(gap * 10) / 10
  const absText = Math.abs(rounded).toFixed(1)
  let valueText: string
  if (rounded > 0) valueText = t('diagnosis_gap_value_negative', { value: absText })
  else if (rounded < 0) valueText = t('diagnosis_gap_value_positive', { value: absText })
  else valueText = t('diagnosis_gap_value_zero')

  // Pick the chip label + whether to show the numeric value. Aligned
  // sub-variants are gated on the card's `insightKey` so the chip and the
  // body sentence below it always tell the same story (otherwise a
  // "lockstep" body insight could end up next to an `Amplify gap ±0` chip).
  let labelText: string
  let showValue = true
  if (isTopOfBucket) {
    labelText = t('diagnosis_top_gap_chip')
  } else if (category === 'aligned') {
    if (insightKey === 'diagnosis_aligned_strong') {
      labelText = t('diagnosis_strong_label')
      showValue = false
    } else if (insightKey === 'diagnosis_aligned_balanced') {
      labelText = t('diagnosis_in_sync_label')
      showValue = false
    } else if (insightKey === 'diagnosis_aligned_creative_lead') {
      labelText = t('diagnosis_creatives_lead_label')
    } else {
      labelText = t('diagnosis_amplify_label')
    }
  } else {
    labelText = t('diagnosis_gap_label')
  }

  return <DiagnosisGapChip label={labelText} value={showValue ? valueText : undefined} accent={accent} filled={isTopOfBucket} />
}
